import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Spinner, Badge, Alert } from "react-bootstrap";
import ModalConfirmar from "../../components/ModalConfirmar";
import * as monedasApi from "../../api/monedas";

export default function MonedasMant() {
  const [monedas, setMonedas] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [guardandoFamiliaId, setGuardandoFamiliaId] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);
  const [form, setForm] = useState({ codigo: "", nombre: "", simbolo: "", activo: true, predeterminada: false });
  const [guardando, setGuardando] = useState(false);
  const [errorModal, setErrorModal] = useState("");
  const [error, setError] = useState("");

  const cargar = async () => {
    setCargando(true);
    try {
      const [listaMonedas, listaFamilias] = await Promise.all([monedasApi.listarMonedas(), monedasApi.listarFamiliasMoneda()]);
      setMonedas(listaMonedas);
      setFamilias(listaFamilias);
    }
    finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, []);

  // Cada cambio se guarda al elegir la moneda (sin botón aparte); si falla, se recarga para volver al valor real.
  const cambiarMonedaFamilia = async (familia, monedaId) => {
    setError("");
    setGuardandoFamiliaId(familia.familiaId);
    try {
      await monedasApi.cambiarMonedaFamilia(familia.familiaId, monedaId);
      setFamilias((prev) => prev.map((f) => (f.familiaId === familia.familiaId ? { ...f, monedaId } : f)));
    } catch (err) {
      setError(err.response?.data?.mensaje || "No se pudo cambiar la moneda de la familia.");
      await cargar();
    } finally {
      setGuardandoFamiliaId(null);
    }
  };

  const abrir = (m) => {
    setErrorModal("");
    setEditando(m);
    setForm(m
      ? { codigo: m.codigo, nombre: m.nombre, simbolo: m.simbolo, activo: m.activo, predeterminada: m.predeterminada }
      : { codigo: "", nombre: "", simbolo: "", activo: true, predeterminada: false });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setErrorModal("");
    try {
      await monedasApi.upsertMoneda({ monedaId: editando?.monedaId ?? null, ...form });
      setShowModal(false);
      cargar();
    } catch (err) {
      setErrorModal(err.response?.data?.mensaje || "No se pudo guardar la moneda.");
    } finally { setGuardando(false); }
  };

  const confirmarEliminar = async () => {
    try {
      await monedasApi.eliminarMoneda(aEliminar.monedaId);
      setAEliminar(null);
      cargar();
    } catch (err) {
      setAEliminar(null);
      setError(err.response?.data?.mensaje || "No se pudo desactivar la moneda.");
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Mantenimiento &gt; Monedas</h4>
        <Button size="sm" variant="primary" onClick={() => abrir(null)}><i className="bi bi-plus-lg me-1"></i>Nueva moneda</Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

      {cargando ? <Spinner animation="border" /> : (
        <div className="fs-card p-0 overflow-hidden">
          <Table responsive hover className="table-fs table-fs-responsive mb-0 align-middle">
            <thead><tr><th>Código</th><th>Nombre</th><th>Símbolo</th><th>Estado</th><th title="La usan los usuarios sin familia y se ofrece al crear una familia">Por defecto del sistema</th><th></th></tr></thead>
            <tbody>
              {monedas.map((m) => (
                <tr key={m.monedaId}>
                  <td data-label="Código" className="fw-semibold">{m.codigo}</td>
                  <td data-label="Nombre">{m.nombre}</td>
                  <td data-label="Símbolo">{m.simbolo}</td>
                  <td data-label="Estado"><Badge bg={m.activo ? "success" : "secondary"}>{m.activo ? "Activa" : "Inactiva"}</Badge></td>
                  <td data-label="Por defecto del sistema">
                    {m.predeterminada && <i className="bi bi-star-fill text-fs-alerta" title="Moneda por defecto del sistema"></i>}
                  </td>
                  <td data-label="Acciones" className="text-end">
                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => abrir(m)}><i className="bi bi-pencil"></i></Button>
                    <Button size="sm" variant="outline-danger" onClick={() => setAEliminar(m)}><i className="bi bi-trash"></i></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {!cargando && (
        <div className="mt-4">
          <h5 className="mb-1">Moneda por familia</h5>
          <p className="text-muted small mb-2">
            Cada familia usa su propia moneda en todas sus pantallas y en la app móvil. Elige otra en la lista y se guarda al instante.
          </p>
          <div className="fs-card p-0 overflow-hidden">
            <Table responsive hover className="table-fs table-fs-responsive mb-0 align-middle">
              <thead><tr><th>Familia</th><th>Jefe</th><th>Miembros</th><th style={{ minWidth: 220 }}>Moneda</th></tr></thead>
              <tbody>
                {familias.map((f) => (
                  <tr key={f.familiaId}>
                    <td data-label="Familia" className="fw-semibold">{f.nombre}</td>
                    <td data-label="Jefe">{f.nombreJefe}<div className="text-muted small">{f.emailJefe}</div></td>
                    <td data-label="Miembros">{f.cantidadMiembros}</td>
                    <td data-label="Moneda">
                      <div className="d-flex align-items-center gap-2">
                        <Form.Select
                          size="sm"
                          value={f.monedaId}
                          disabled={guardandoFamiliaId === f.familiaId}
                          onChange={(e) => cambiarMonedaFamilia(f, Number(e.target.value))}
                          aria-label={`Moneda de la familia ${f.nombre}`}
                        >
                          {monedas.filter((m) => m.activo || m.monedaId === f.monedaId).map((m) => (
                            <option key={m.monedaId} value={m.monedaId}>{m.codigo} — {m.nombre} ({m.simbolo})</option>
                          ))}
                        </Form.Select>
                        {guardandoFamiliaId === f.familiaId && <Spinner size="sm" animation="border" />}
                      </div>
                    </td>
                  </tr>
                ))}
                {!familias.length && (
                  <tr><td colSpan={4} className="text-center text-muted py-4">Todavía no hay familias creadas.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton><Modal.Title style={{ fontSize: "1.1rem" }}>{editando ? "Editar moneda" : "Nueva moneda"}</Modal.Title></Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {errorModal && <Alert variant="danger" dismissible onClose={() => setErrorModal("")}>{errorModal}</Alert>}
            <div className="row">
              <div className="col-4">
                <Form.Group className="mb-3">
                  <Form.Label>Código</Form.Label>
                  <Form.Control value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} maxLength={3} required />
                </Form.Group>
              </div>
              <div className="col-8">
                <Form.Group className="mb-3">
                  <Form.Label>Nombre</Form.Label>
                  <Form.Control value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Símbolo</Form.Label>
              <Form.Control value={form.simbolo} onChange={(e) => setForm({ ...form, simbolo: e.target.value })} required style={{ maxWidth: 100 }} />
            </Form.Group>
            <Form.Check type="switch" label="Activa" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} className="mb-2" />
            <Form.Check type="switch" label="Moneda por defecto del sistema" checked={form.predeterminada}
              onChange={(e) => setForm({ ...form, predeterminada: e.target.checked })} />
            <div className="text-muted small mt-1">
              La usan los usuarios que no pertenecen a ninguna familia y se ofrece preseleccionada al crear una.
              Cada familia elige la suya aparte (sección "Moneda por familia").
            </div>
            {form.predeterminada && (
              <div className="text-muted small mt-1">Al guardar, se quitará como "por defecto" cualquier otra moneda.</div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={guardando}>{guardando ? <Spinner size="sm" animation="border" /> : "Guardar"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ModalConfirmar show={!!aEliminar} titulo="Desactivar moneda" mensaje={`¿Desactivar "${aEliminar?.nombre}"?`}
        onConfirmar={confirmarEliminar} onCancelar={() => setAEliminar(null)} />
    </div>
  );
}
